/**
 * Style Resolver
 *
 * Converts Styles Builder selections into concrete design token values.
 * Used by the preview renderer and prompt generator to ensure consistency.
 */

import type {
  StyleType,
  ThemeType,
  AccentColor,
  TypographyType,
  SpacingType,
  AnimationType,
  CornerStyleType,
  ShadowType,
} from "@/store/zustand/stylesBuilderStore";
import {
  ACCENT_COLOR_OPTIONS,
  TYPOGRAPHY_OPTIONS,
} from "@/store/zustand/stylesBuilderStore";

// ============================================================================
// Design Token Types
// ============================================================================

export interface ColorTokens {
  background: string;
  backgroundCard: string;
  backgroundMuted: string;
  backgroundHover: string;
  text: string;
  textMuted: string;
  border: string;
  inputBackground: string;
  accent: string;
  accentHover: string;
  accentText: string;
  // Glass effect (only populated when style is "glass")
  glassBackground?: string;
  glassBorder?: string;
}

export interface RadiusTokens {
  sm: string;
  md: string;
  lg: string;
}

export interface ShadowTokens {
  sm: string;
  md: string;
  lg: string;
  card: string; // The shadow applied to cards
}

export interface SpacingTokens {
  unit: number;
  multiplier: number;
  scale: Record<string, string>;
}

export interface TypographyTokens {
  fontFamily: string;
  fontFamilyMono: string;
}

export interface AnimationTokens {
  enabled: boolean;
  duration: string;
  easing: string;
  hoverTransform: string;
  hoverScale: string;
}

export interface DesignTokens {
  colors: ColorTokens;
  radius: RadiusTokens;
  shadow: ShadowTokens;
  spacing: SpacingTokens;
  typography: TypographyTokens;
  animation: AnimationTokens;
  // Meta information
  style: StyleType | null;
  theme: ThemeType | null;
}

// ============================================================================
// Selection Types (input to resolver)
// ============================================================================

export interface StyleSelections {
  style: StyleType | null;
  theme: ThemeType | null;
  accentColor: AccentColor | null;
  typography: TypographyType | null;
  spacing: SpacingType | null;
  animation: AnimationType | null;
  cornerStyle: CornerStyleType | null;
  shadow: ShadowType | null;
  customAccentHex?: string | null;
}

// ============================================================================
// Token Resolvers
// ============================================================================

function resolveAccentHex(
  accentColor: AccentColor | null,
  customAccentHex?: string | null,
  style?: StyleType | null,
): string {
  // Custom hex takes priority
  if (customAccentHex) return customAccentHex;

  // User-selected accent color
  if (accentColor) {
    return (
      ACCENT_COLOR_OPTIONS.find((c) => c.value === accentColor)?.hex ||
      "#E63946"
    );
  }

  switch (style) {
    case "minimal":
      return "#E63946";
    case "bold":
      return "#FF2D92";
    case "soft":
      return "#E07A5F";
    case "elegant":
      return "#D4A853";
    case "corporate":
      return "#0D9488";
    case "glass":
      return "#06B6D4";
    default:
      return "#D4A853";
  }
}

function resolveColors(
  theme: ThemeType | null,
  style: StyleType | null,
  accentColor: AccentColor | null,
  customAccentHex?: string | null,
): ColorTokens {
  const accent = resolveAccentHex(accentColor, customAccentHex, style);
  const isDark = theme === "dark" || theme === "both";

  let baseColors: Omit<
    ColorTokens,
    "accent" | "accentHover" | "accentText" | "glassBackground" | "glassBorder"
  >;

  if (isDark) {
    switch (style) {
      case "minimal":
        baseColors = {
          background: "#000000",
          backgroundCard: "#000000",
          backgroundMuted: "#0a0a0a",
          backgroundHover: "#141414",
          text: "#ffffff",
          textMuted: "#666666",
          border: "#1a1a1a",
          inputBackground: "#000000",
        };
        break;
      case "bold":
        baseColors = {
          background: "#030304",
          backgroundCard: "#0d0d12",
          backgroundMuted: "#12121a",
          backgroundHover: "#1a1a28",
          text: "#f0f0f5",
          textMuted: "#7a7a8c",
          border: "#252535",
          inputBackground: "#08080c",
        };
        break;
      case "soft":
        baseColors = {
          background: "#1a1520",
          backgroundCard: "#221c28",
          backgroundMuted: "#2a2333",
          backgroundHover: "#352d40",
          text: "#f5f0fa",
          textMuted: "#9d8fb0",
          border: "#3d3450",
          inputBackground: "#1e1825",
        };
        break;
      case "elegant":
        baseColors = {
          background: "#0f0d0a",
          backgroundCard: "#1a1714",
          backgroundMuted: "#24201a",
          backgroundHover: "#302b24",
          text: "#faf5eb",
          textMuted: "#a69d8c",
          border: "#3d3628",
          inputBackground: "#141210",
        };
        break;
      case "corporate":
        baseColors = {
          background: "#0c1015",
          backgroundCard: "#111820",
          backgroundMuted: "#182028",
          backgroundHover: "#1f2a35",
          text: "#e8eef5",
          textMuted: "#7a8fa5",
          border: "#253040",
          inputBackground: "#0f151c",
        };
        break;
      case "glass":
        baseColors = {
          background: "#050510",
          backgroundCard: "#0a0a18",
          backgroundMuted: "#101025",
          backgroundHover: "#181830",
          text: "#e8e8ff",
          textMuted: "#8888aa",
          border: "#202040",
          inputBackground: "#08081a",
        };
        break;
      default:
        baseColors = {
          background: "#0a0a0c",
          backgroundCard: "#141418",
          backgroundMuted: "#1e1e24",
          backgroundHover: "#28282f",
          text: "#f4f4f6",
          textMuted: "#8e8e98",
          border: "#2a2a32",
          inputBackground: "#101014",
        };
    }
  } else {
    switch (style) {
      case "minimal":
        baseColors = {
          background: "#fafafa",
          backgroundCard: "#ffffff",
          backgroundMuted: "#f5f5f5",
          backgroundHover: "#efefef",
          text: "#121212",
          textMuted: "#5c5c5c",
          border: "#e0e0e0",
          inputBackground: "#ffffff",
        };
        break;
      case "bold":
        baseColors = {
          background: "#fffbf5",
          backgroundCard: "#ffffff",
          backgroundMuted: "#fff5e8",
          backgroundHover: "#ffedd5",
          text: "#1a1005",
          textMuted: "#755830",
          border: "#f5dfc4",
          inputBackground: "#ffffff",
        };
        break;
      case "soft":
        baseColors = {
          background: "#faf8ff",
          backgroundCard: "#ffffff",
          backgroundMuted: "#f3f0fa",
          backgroundHover: "#ebe5f5",
          text: "#2d2540",
          textMuted: "#6d6580",
          border: "#e0d8f0",
          inputBackground: "#fcfaff",
        };
        break;
      case "elegant":
        baseColors = {
          background: "#fdfbf7",
          backgroundCard: "#fffefa",
          backgroundMuted: "#f8f4ed",
          backgroundHover: "#f2ece2",
          text: "#2c2418",
          textMuted: "#7a6d5c",
          border: "#e8dfd0",
          inputBackground: "#fffdf8",
        };
        break;
      case "corporate":
        baseColors = {
          background: "#f5f8fc",
          backgroundCard: "#ffffff",
          backgroundMuted: "#eef2f8",
          backgroundHover: "#e4eaf2",
          text: "#0d1a2d",
          textMuted: "#506580",
          border: "#d5e0ed",
          inputBackground: "#ffffff",
        };
        break;
      case "glass":
        baseColors = {
          background: "#f0f2f5",
          backgroundCard: "#ffffff",
          backgroundMuted: "#e8ecf2",
          backgroundHover: "#dde3eb",
          text: "#151820",
          textMuted: "#5a6578",
          border: "#d0d8e5",
          inputBackground: "#f8fafc",
        };
        break;
      default:
        baseColors = {
          background: "#f8f8fa",
          backgroundCard: "#ffffff",
          backgroundMuted: "#f0f0f4",
          backgroundHover: "#e8e8ed",
          text: "#151518",
          textMuted: "#6b6b75",
          border: "#dddde2",
          inputBackground: "#ffffff",
        };
    }
  }

  const colors: ColorTokens = {
    ...baseColors,
    accent,
    accentHover: accent + "dd",
    accentText: "#ffffff",
  };

  // Add glass effect tokens if style is glass
  if (style === "glass") {
    colors.glassBackground = isDark
      ? "rgba(255,255,255,0.06)"
      : "rgba(255,255,255,0.7)";
    colors.glassBorder = isDark
      ? "rgba(255,255,255,0.12)"
      : "rgba(255,255,255,0.5)";
  }

  return colors;
}

function resolveRadius(
  cornerStyle: CornerStyleType | null,
  style: StyleType | null,
): RadiusTokens {
  if (cornerStyle) {
    let baseRadius: string;
    switch (cornerStyle) {
      case "none":
        baseRadius = "0px";
        break;
      case "small":
        baseRadius = "4px";
        break;
      case "medium":
        baseRadius = "10px";
        break;
      case "large":
        baseRadius = "16px";
        break;
      default:
        baseRadius = "8px";
    }
    const numericValue = parseFloat(baseRadius);
    return {
      sm: `${Math.round(numericValue * 0.5)}px`,
      md: baseRadius,
      lg: `${Math.round(numericValue * 1.5)}px`,
    };
  }

  switch (style) {
    case "minimal":
      return { sm: "0px", md: "2px", lg: "4px" };
    case "bold":
      return { sm: "4px", md: "8px", lg: "12px" };
    case "soft":
      return { sm: "12px", md: "20px", lg: "28px" };
    case "elegant":
      return { sm: "4px", md: "10px", lg: "16px" };
    case "corporate":
      return { sm: "3px", md: "6px", lg: "10px" };
    case "glass":
      return { sm: "10px", md: "18px", lg: "24px" };
    default:
      return { sm: "4px", md: "8px", lg: "12px" };
  }
}

function resolveShadow(
  shadow: ShadowType | null,
  style: StyleType | null,
  theme: ThemeType | null,
): ShadowTokens {
  const isDark = theme === "dark" || theme === "both";

  // User explicitly selected "none" - always respect this
  if (shadow === "none") {
    return { sm: "none", md: "none", lg: "none", card: "none" };
  }

  // Minimal style defaults to no shadow unless user explicitly selects one
  if (shadow === null && style === "minimal") {
    return { sm: "none", md: "none", lg: "none", card: "none" };
  }

  // Determine effective intensity: user selection or style-based default
  const effectiveShadow = shadow ?? getStyleDefaultShadow(style);

  // Get style-specific shadow definitions
  const styleShadows = getStyleShadowDefinitions(style, isDark);

  // Return shadows based on intensity
  switch (effectiveShadow) {
    case "none":
      return { sm: "none", md: "none", lg: "none", card: "none" };
    case "subtle":
      return {
        sm: styleShadows.subtle.sm,
        md: styleShadows.subtle.md,
        lg: styleShadows.subtle.lg,
        card: styleShadows.subtle.card,
      };
    case "medium":
      return {
        sm: styleShadows.medium.sm,
        md: styleShadows.medium.md,
        lg: styleShadows.medium.lg,
        card: styleShadows.medium.card,
      };
    case "strong":
      return {
        sm: styleShadows.strong.sm,
        md: styleShadows.strong.md,
        lg: styleShadows.strong.lg,
        card: styleShadows.strong.card,
      };
    default:
      return styleShadows.medium;
  }
}

function getStyleDefaultShadow(style: StyleType | null): ShadowType {
  switch (style) {
    case "minimal":
      return "none";
    case "bold":
      return "strong";
    case "soft":
      return "medium";
    case "elegant":
      return "subtle";
    case "corporate":
      return "subtle";
    case "glass":
      return "medium";
    default:
      return "subtle";
  }
}

interface ShadowIntensitySet {
  sm: string;
  md: string;
  lg: string;
  card: string;
}

interface StyleShadowDefinitions {
  subtle: ShadowIntensitySet;
  medium: ShadowIntensitySet;
  strong: ShadowIntensitySet;
}

function getStyleShadowDefinitions(
  style: StyleType | null,
  isDark: boolean,
): StyleShadowDefinitions {
  switch (style) {
    case "minimal":
      return {
        subtle: {
          sm: "1px 1px 0 rgba(0,0,0,0.1)",
          md: "2px 2px 0 rgba(0,0,0,0.12)",
          lg: "3px 3px 0 rgba(0,0,0,0.15)",
          card: "2px 2px 0 rgba(0,0,0,0.1)",
        },
        medium: {
          sm: "2px 2px 0 rgba(0,0,0,0.15)",
          md: "4px 4px 0 rgba(0,0,0,0.18)",
          lg: "6px 6px 0 rgba(0,0,0,0.2)",
          card: "4px 4px 0 rgba(0,0,0,0.15)",
        },
        strong: {
          sm: "3px 3px 0 rgba(0,0,0,0.2)",
          md: "6px 6px 0 rgba(0,0,0,0.22)",
          lg: "10px 10px 0 rgba(0,0,0,0.25)",
          card: "6px 6px 0 rgba(0,0,0,0.2)",
        },
      };

    case "bold": {
      const glowColor = isDark
        ? "rgba(255,45,146,0.3)"
        : "rgba(255,45,146,0.12)";
      return {
        subtle: {
          sm: isDark
            ? `0 2px 6px rgba(0,0,0,0.4), 0 0 12px ${glowColor}`
            : "0 2px 6px rgba(0,0,0,0.08)",
          md: isDark
            ? `0 4px 12px rgba(0,0,0,0.5), 0 0 20px ${glowColor}`
            : "0 4px 12px rgba(0,0,0,0.1)",
          lg: isDark
            ? `0 8px 24px rgba(0,0,0,0.55), 0 0 30px ${glowColor}`
            : "0 8px 24px rgba(0,0,0,0.12)",
          card: isDark
            ? `0 4px 16px rgba(0,0,0,0.5), 0 0 16px ${glowColor}`
            : "0 4px 16px rgba(0,0,0,0.1)",
        },
        medium: {
          sm: isDark
            ? `0 4px 10px rgba(0,0,0,0.5), 0 0 20px ${glowColor}`
            : "0 4px 10px rgba(0,0,0,0.1)",
          md: isDark
            ? `0 8px 24px rgba(0,0,0,0.6), 0 0 36px ${glowColor}`
            : "0 8px 24px rgba(0,0,0,0.14)",
          lg: isDark
            ? `0 16px 40px rgba(0,0,0,0.65), 0 0 50px ${glowColor}`
            : "0 16px 40px rgba(0,0,0,0.18)",
          card: isDark
            ? `0 8px 28px rgba(0,0,0,0.6), 0 0 28px ${glowColor}`
            : "0 8px 28px rgba(0,0,0,0.12)",
        },
        strong: {
          sm: isDark
            ? `0 6px 14px rgba(0,0,0,0.55), 0 0 30px ${glowColor}`
            : "0 6px 14px rgba(0,0,0,0.12)",
          md: isDark
            ? `0 12px 32px rgba(0,0,0,0.7), 0 0 50px ${glowColor}`
            : "0 12px 32px rgba(0,0,0,0.18)",
          lg: isDark
            ? `0 24px 56px rgba(0,0,0,0.75), 0 0 70px ${glowColor}`
            : "0 24px 56px rgba(0,0,0,0.22)",
          card: isDark
            ? `0 12px 40px rgba(0,0,0,0.7), 0 0 40px ${glowColor}`
            : "0 12px 40px rgba(0,0,0,0.16)",
        },
      };
    }

    case "soft":
      return {
        subtle: {
          sm: isDark
            ? "0 2px 6px rgba(100,80,140,0.15), 0 3px 12px rgba(0,0,0,0.1)"
            : "0 2px 6px rgba(100,80,140,0.06), 0 3px 12px rgba(0,0,0,0.03)",
          md: isDark
            ? "0 3px 10px rgba(100,80,140,0.18), 0 6px 20px rgba(0,0,0,0.12)"
            : "0 3px 10px rgba(100,80,140,0.08), 0 6px 20px rgba(0,0,0,0.04)",
          lg: isDark
            ? "0 5px 16px rgba(100,80,140,0.22), 0 10px 32px rgba(0,0,0,0.15)"
            : "0 5px 16px rgba(100,80,140,0.1), 0 10px 32px rgba(0,0,0,0.05)",
          card: isDark
            ? "0 3px 14px rgba(100,80,140,0.18), 0 6px 24px rgba(0,0,0,0.12)"
            : "0 3px 14px rgba(100,80,140,0.07), 0 6px 24px rgba(0,0,0,0.03)",
        },
        medium: {
          sm: isDark
            ? "0 3px 10px rgba(100,80,140,0.2), 0 5px 18px rgba(0,0,0,0.14)"
            : "0 3px 10px rgba(100,80,140,0.08), 0 5px 18px rgba(0,0,0,0.04)",
          md: isDark
            ? "0 5px 16px rgba(100,80,140,0.25), 0 10px 32px rgba(0,0,0,0.18)"
            : "0 5px 16px rgba(100,80,140,0.1), 0 10px 32px rgba(0,0,0,0.06)",
          lg: isDark
            ? "0 8px 28px rgba(100,80,140,0.3), 0 16px 48px rgba(0,0,0,0.22)"
            : "0 8px 28px rgba(100,80,140,0.12), 0 16px 48px rgba(0,0,0,0.08)",
          card: isDark
            ? "0 5px 20px rgba(100,80,140,0.25), 0 10px 36px rgba(0,0,0,0.18)"
            : "0 5px 20px rgba(100,80,140,0.1), 0 10px 36px rgba(0,0,0,0.05)",
        },
        strong: {
          sm: isDark
            ? "0 4px 14px rgba(100,80,140,0.28), 0 8px 24px rgba(0,0,0,0.2)"
            : "0 4px 14px rgba(100,80,140,0.12), 0 8px 24px rgba(0,0,0,0.06)",
          md: isDark
            ? "0 8px 24px rgba(100,80,140,0.35), 0 16px 48px rgba(0,0,0,0.25)"
            : "0 8px 24px rgba(100,80,140,0.15), 0 16px 48px rgba(0,0,0,0.08)",
          lg: isDark
            ? "0 12px 40px rgba(100,80,140,0.4), 0 24px 64px rgba(0,0,0,0.3)"
            : "0 12px 40px rgba(100,80,140,0.18), 0 24px 64px rgba(0,0,0,0.1)",
          card: isDark
            ? "0 8px 32px rgba(100,80,140,0.35), 0 16px 52px rgba(0,0,0,0.25)"
            : "0 8px 32px rgba(100,80,140,0.14), 0 16px 52px rgba(0,0,0,0.07)",
        },
      };

    case "elegant":
      return {
        subtle: {
          sm: isDark
            ? "0 1px 2px rgba(0,0,0,0.3), 0 2px 4px rgba(40,30,20,0.15)"
            : "0 1px 2px rgba(40,30,20,0.05)",
          md: isDark
            ? "0 2px 6px rgba(0,0,0,0.35), 0 4px 10px rgba(40,30,20,0.18)"
            : "0 2px 6px rgba(40,30,20,0.06)",
          lg: isDark
            ? "0 4px 12px rgba(0,0,0,0.4), 0 8px 20px rgba(40,30,20,0.2)"
            : "0 4px 12px rgba(40,30,20,0.08)",
          card: isDark
            ? "0 2px 8px rgba(0,0,0,0.35), 0 4px 14px rgba(40,30,20,0.15)"
            : "0 2px 6px rgba(40,30,20,0.05)",
        },
        medium: {
          sm: isDark
            ? "0 2px 4px rgba(0,0,0,0.4), 0 3px 8px rgba(40,30,20,0.2)"
            : "0 2px 4px rgba(40,30,20,0.07)",
          md: isDark
            ? "0 4px 12px rgba(0,0,0,0.5), 0 8px 20px rgba(40,30,20,0.25)"
            : "0 4px 12px rgba(40,30,20,0.1)",
          lg: isDark
            ? "0 8px 24px rgba(0,0,0,0.55), 0 16px 36px rgba(40,30,20,0.28)"
            : "0 8px 24px rgba(40,30,20,0.12)",
          card: isDark
            ? "0 4px 16px rgba(0,0,0,0.5), 0 8px 24px rgba(40,30,20,0.2)"
            : "0 4px 12px rgba(40,30,20,0.08)",
        },
        strong: {
          sm: isDark
            ? "0 3px 6px rgba(0,0,0,0.5), 0 5px 12px rgba(40,30,20,0.28)"
            : "0 3px 6px rgba(40,30,20,0.1)",
          md: isDark
            ? "0 6px 18px rgba(0,0,0,0.6), 0 12px 28px rgba(40,30,20,0.32)"
            : "0 6px 18px rgba(40,30,20,0.14)",
          lg: isDark
            ? "0 12px 36px rgba(0,0,0,0.65), 0 24px 48px rgba(40,30,20,0.35)"
            : "0 12px 36px rgba(40,30,20,0.16)",
          card: isDark
            ? "0 6px 24px rgba(0,0,0,0.6), 0 12px 36px rgba(40,30,20,0.28)"
            : "0 6px 20px rgba(40,30,20,0.12)",
        },
      };

    case "corporate":
      return {
        subtle: {
          sm: isDark
            ? "0 1px 2px rgba(0,0,0,0.3), 0 1px 3px rgba(0,20,40,0.2)"
            : "0 1px 2px rgba(0,20,40,0.04)",
          md: isDark
            ? "0 2px 5px rgba(0,0,0,0.4), 0 4px 10px rgba(0,20,40,0.25)"
            : "0 2px 5px rgba(0,20,40,0.06)",
          lg: isDark
            ? "0 4px 10px rgba(0,0,0,0.45), 0 8px 18px rgba(0,20,40,0.28)"
            : "0 4px 10px rgba(0,20,40,0.07)",
          card: isDark
            ? "0 2px 6px rgba(0,0,0,0.4), 0 4px 12px rgba(0,20,40,0.22)"
            : "0 2px 6px rgba(0,20,40,0.05)",
        },
        medium: {
          sm: isDark
            ? "0 2px 4px rgba(0,0,0,0.4), 0 3px 6px rgba(0,20,40,0.28)"
            : "0 2px 4px rgba(0,20,40,0.06)",
          md: isDark
            ? "0 4px 10px rgba(0,0,0,0.5), 0 8px 18px rgba(0,20,40,0.35)"
            : "0 4px 10px rgba(0,20,40,0.08)",
          lg: isDark
            ? "0 8px 18px rgba(0,0,0,0.55), 0 16px 32px rgba(0,20,40,0.38)"
            : "0 8px 18px rgba(0,20,40,0.1)",
          card: isDark
            ? "0 4px 12px rgba(0,0,0,0.5), 0 8px 20px rgba(0,20,40,0.3)"
            : "0 4px 12px rgba(0,20,40,0.07)",
        },
        strong: {
          sm: isDark
            ? "0 3px 6px rgba(0,0,0,0.5), 0 5px 10px rgba(0,20,40,0.35)"
            : "0 3px 6px rgba(0,20,40,0.08)",
          md: isDark
            ? "0 6px 14px rgba(0,0,0,0.6), 0 12px 26px rgba(0,20,40,0.42)"
            : "0 6px 14px rgba(0,20,40,0.1)",
          lg: isDark
            ? "0 12px 28px rgba(0,0,0,0.65), 0 24px 48px rgba(0,20,40,0.45)"
            : "0 12px 28px rgba(0,20,40,0.12)",
          card: isDark
            ? "0 6px 18px rgba(0,0,0,0.6), 0 12px 32px rgba(0,20,40,0.38)"
            : "0 6px 18px rgba(0,20,40,0.1)",
        },
      };

    case "glass":
      return {
        subtle: {
          sm: isDark
            ? "0 2px 6px rgba(0,0,30,0.3), inset 0 1px 0 rgba(255,255,255,0.04)"
            : "0 2px 6px rgba(0,0,30,0.06), inset 0 1px 0 rgba(255,255,255,0.4)",
          md: isDark
            ? "0 4px 14px rgba(0,0,30,0.38), inset 0 1px 0 rgba(255,255,255,0.05)"
            : "0 4px 14px rgba(0,0,30,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
          lg: isDark
            ? "0 8px 24px rgba(0,0,30,0.45), inset 0 1px 0 rgba(255,255,255,0.06)"
            : "0 8px 24px rgba(0,0,30,0.1), inset 0 1px 0 rgba(255,255,255,0.6)",
          card: isDark
            ? "0 4px 18px rgba(0,0,30,0.38), inset 0 1px 0 rgba(255,255,255,0.04)"
            : "0 4px 18px rgba(0,0,30,0.07), inset 0 1px 0 rgba(255,255,255,0.45)",
        },
        medium: {
          sm: isDark
            ? "0 3px 10px rgba(0,0,30,0.4), inset 0 1px 0 rgba(255,255,255,0.05)"
            : "0 3px 10px rgba(0,0,30,0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
          md: isDark
            ? "0 8px 24px rgba(0,0,30,0.5), inset 0 1px 0 rgba(255,255,255,0.07)"
            : "0 8px 24px rgba(0,0,30,0.1), inset 0 1px 0 rgba(255,255,255,0.65)",
          lg: isDark
            ? "0 16px 40px rgba(0,0,30,0.55), inset 0 1px 0 rgba(255,255,255,0.08)"
            : "0 16px 40px rgba(0,0,30,0.12), inset 0 2px 0 rgba(255,255,255,0.75)",
          card: isDark
            ? "0 8px 28px rgba(0,0,30,0.5), inset 0 1px 0 rgba(255,255,255,0.06)"
            : "0 8px 28px rgba(0,0,30,0.09), inset 0 1px 0 rgba(255,255,255,0.6)",
        },
        strong: {
          sm: isDark
            ? "0 4px 14px rgba(0,0,30,0.5), inset 0 1px 0 rgba(255,255,255,0.07)"
            : "0 4px 14px rgba(0,0,30,0.1), inset 0 1px 0 rgba(255,255,255,0.6)",
          md: isDark
            ? "0 12px 36px rgba(0,0,30,0.6), inset 0 1px 0 rgba(255,255,255,0.09)"
            : "0 12px 36px rgba(0,0,30,0.14), inset 0 2px 0 rgba(255,255,255,0.75)",
          lg: isDark
            ? "0 24px 56px rgba(0,0,30,0.65), inset 0 2px 0 rgba(255,255,255,0.1)"
            : "0 24px 56px rgba(0,0,30,0.16), inset 0 2px 0 rgba(255,255,255,0.85)",
          card: isDark
            ? "0 12px 40px rgba(0,0,30,0.6), inset 0 1px 0 rgba(255,255,255,0.08)"
            : "0 12px 40px rgba(0,0,30,0.12), inset 0 2px 0 rgba(255,255,255,0.7)",
        },
      };

    default:
      return {
        subtle: {
          sm: isDark
            ? "0 1px 3px rgba(0,0,0,0.3)"
            : "0 1px 3px rgba(0,0,0,0.06)",
          md: isDark
            ? "0 2px 8px rgba(0,0,0,0.4)"
            : "0 2px 8px rgba(0,0,0,0.08)",
          lg: isDark
            ? "0 4px 16px rgba(0,0,0,0.45)"
            : "0 4px 16px rgba(0,0,0,0.1)",
          card: isDark
            ? "0 2px 10px rgba(0,0,0,0.4)"
            : "0 2px 10px rgba(0,0,0,0.07)",
        },
        medium: {
          sm: isDark
            ? "0 2px 5px rgba(0,0,0,0.4)"
            : "0 2px 5px rgba(0,0,0,0.08)",
          md: isDark
            ? "0 4px 14px rgba(0,0,0,0.5)"
            : "0 4px 14px rgba(0,0,0,0.1)",
          lg: isDark
            ? "0 8px 28px rgba(0,0,0,0.55)"
            : "0 8px 28px rgba(0,0,0,0.12)",
          card: isDark
            ? "0 4px 18px rgba(0,0,0,0.5)"
            : "0 4px 18px rgba(0,0,0,0.1)",
        },
        strong: {
          sm: isDark
            ? "0 3px 8px rgba(0,0,0,0.5)"
            : "0 3px 8px rgba(0,0,0,0.1)",
          md: isDark
            ? "0 6px 20px rgba(0,0,0,0.6)"
            : "0 6px 20px rgba(0,0,0,0.12)",
          lg: isDark
            ? "0 12px 40px rgba(0,0,0,0.65)"
            : "0 12px 40px rgba(0,0,0,0.15)",
          card: isDark
            ? "0 6px 26px rgba(0,0,0,0.6)"
            : "0 6px 26px rgba(0,0,0,0.12)",
        },
      };
  }
}

function resolveSpacing(spacing: SpacingType | null): SpacingTokens {
  let multiplier: number;

  switch (spacing) {
    case "compact":
      multiplier = 0.75;
      break;
    case "spacious":
      multiplier = 1.25;
      break;
    case "comfortable":
    default:
      multiplier = 1;
  }

  const unit = 4; // Base unit in pixels

  return {
    unit,
    multiplier,
    scale: {
      "1": `${unit * 1 * multiplier}px`,
      "2": `${unit * 2 * multiplier}px`,
      "3": `${unit * 3 * multiplier}px`,
      "4": `${unit * 4 * multiplier}px`,
      "5": `${unit * 5 * multiplier}px`,
      "6": `${unit * 6 * multiplier}px`,
      "8": `${unit * 8 * multiplier}px`,
      "10": `${unit * 10 * multiplier}px`,
      "12": `${unit * 12 * multiplier}px`,
    },
  };
}

function resolveTypography(
  typography: TypographyType | null,
  style: StyleType | null,
): TypographyTokens {
  let fontFamily: string;

  if (typography) {
    // Look up font family from TYPOGRAPHY_OPTIONS
    const fontOption = TYPOGRAPHY_OPTIONS.find(
      (opt) => opt.value === typography,
    );
    fontFamily = fontOption?.fontFamily || "'DM Sans', sans-serif";
  } else {
    // Style-based defaults when no typography explicitly selected
    switch (style) {
      case "minimal":
        fontFamily = "'Space Grotesk', sans-serif";
        break;
      case "bold":
        fontFamily = "'Space Grotesk', sans-serif";
        break;
      case "soft":
        fontFamily = "'DM Sans', sans-serif";
        break;
      case "elegant":
        fontFamily = "'Playfair Display', serif";
        break;
      case "corporate":
        fontFamily = "'Public Sans', sans-serif";
        break;
      case "glass":
        fontFamily = "'Sora', sans-serif";
        break;
      default:
        fontFamily = "'DM Sans', sans-serif";
    }
  }

  // Determine mono font based on selected typography or default
  const isMonoSelected =
    typography === "jetbrains-mono" ||
    typography === "fira-code" ||
    typography === "ibm-plex-mono";
  const fontFamilyMono = isMonoSelected
    ? fontFamily
    : "'JetBrains Mono', 'Fira Code', monospace";

  return {
    fontFamily,
    fontFamilyMono,
  };
}

function resolveAnimation(animation: AnimationType | null): AnimationTokens {
  switch (animation) {
    case "none":
      return {
        enabled: false,
        duration: "0ms",
        easing: "linear",
        hoverTransform: "none",
        hoverScale: "1",
      };
    case "rich":
      return {
        enabled: true,
        duration: "250ms",
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        hoverTransform: "translateY(-4px) scale(1.01)",
        hoverScale: "1.03",
      };
    case "subtle":
    default:
      return {
        enabled: true,
        duration: "200ms",
        easing: "ease",
        hoverTransform: "translateY(-2px)",
        hoverScale: "1.01",
      };
  }
}

// ============================================================================
// Main Resolver Function
// ============================================================================

/**
 * Resolves user selections into concrete design tokens.
 * This is the single source of truth for all token values.
 */
export function resolveTokens(selections: StyleSelections): DesignTokens {
  return {
    colors: resolveColors(
      selections.theme,
      selections.style,
      selections.accentColor,
      selections.customAccentHex,
    ),
    radius: resolveRadius(selections.cornerStyle, selections.style),
    shadow: resolveShadow(
      selections.shadow,
      selections.style,
      selections.theme,
    ),
    spacing: resolveSpacing(selections.spacing),
    typography: resolveTypography(selections.typography, selections.style),
    animation: resolveAnimation(selections.animation),
    style: selections.style,
    theme: selections.theme,
  };
}

// ============================================================================
// CSS Variable Generation
// ============================================================================

/**
 * Converts design tokens to CSS variables string for use in stylesheets.
 */
export function tokensToCSSVariables(tokens: DesignTokens): string {
  const vars: Record<string, string> = {
    "--bg": tokens.colors.background,
    "--bg-card": tokens.colors.backgroundCard,
    "--bg-muted": tokens.colors.backgroundMuted,
    "--bg-hover": tokens.colors.backgroundHover,
    "--text": tokens.colors.text,
    "--text-muted": tokens.colors.textMuted,
    "--border": tokens.colors.border,
    "--input-bg": tokens.colors.inputBackground,
    "--accent": tokens.colors.accent,
    "--accent-hover": tokens.colors.accentHover,
    "--accent-text": tokens.colors.accentText,
    "--radius": tokens.radius.md,
    "--radius-sm": tokens.radius.sm,
    "--radius-lg": tokens.radius.lg,
    "--shadow": tokens.shadow.card,
    "--shadow-sm": tokens.shadow.sm,
    "--shadow-md": tokens.shadow.md,
    "--shadow-lg": tokens.shadow.lg,
    "--space-1": tokens.spacing.scale["1"],
    "--space-2": tokens.spacing.scale["2"],
    "--space-3": tokens.spacing.scale["3"],
    "--space-4": tokens.spacing.scale["4"],
    "--space-5": tokens.spacing.scale["5"],
    "--space-6": tokens.spacing.scale["6"],
    "--font": tokens.typography.fontFamily,
    "--font-mono": tokens.typography.fontFamilyMono,
  };

  if (tokens.colors.glassBackground) {
    vars["--glass-bg"] = tokens.colors.glassBackground;
  }
  if (tokens.colors.glassBorder) {
    vars["--glass-border"] = tokens.colors.glassBorder;
  }

  return Object.entries(vars)
    .map(([key, value]) => `${key}: ${value};`)
    .join("\n    ");
}

/**
 * Generates animation CSS based on tokens.
 */
export function tokensToAnimationCSS(tokens: DesignTokens): string {
  if (!tokens.animation.enabled) return "";

  return `
    .card:hover {
      transform: ${tokens.animation.hoverTransform};
      box-shadow: ${tokens.shadow.lg};
    }
    .btn:hover {
      transform: scale(${tokens.animation.hoverScale});
    }
    * {
      transition: all ${tokens.animation.duration} ${tokens.animation.easing};
    }
  `;
}
